import { Button } from '@/components/ui/button.tsx'
import { useFetchBillingPortalUrl } from '@/lib/billing/useFetchBillingPortalUrl.ts'
import { useFetchCheckoutUrl } from '@/lib/billing/useFetchCheckoutUrl.ts'
import { useCurrentSubscription } from '@/lib/organizations/useCurrentSubscription.ts'
import * as Dialog from '@/components/ui/dialog.tsx'

import * as Tabs from '@/components/ui/tabs.tsx'

import { useState } from 'react'
import { cn } from '@/lib/utils.ts'
import {
  CircleUser,
  FolderOpen,
  Grid2x2Check,
  LayoutDashboard,
  RefreshCw,
  Unplug,
} from 'lucide-react'
import { LucideIcon } from 'lucide-react'
export function MManageSubscription() {
  const [isOpen, setIsOpen] = useState(false)

  const currentSubscription = useCurrentSubscription()

  useFetchCheckoutUrl()
  const billingPortalQuery = useFetchBillingPortalUrl()

  if (currentSubscription && billingPortalQuery.isSuccess) {
    return (
      <Button variant="outline" size="sm" className="w-full" asChild>
        <a target="_blank" href={billingPortalQuery.data.url} rel="noreferrer">
          Manage subscription
        </a>
      </Button>
    )
  }

  return (
    <Dialog.Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.DialogTrigger asChild>
        <Button variant="outline" className="w-full" size="sm">
          Upgrade
        </Button>
      </Dialog.DialogTrigger>
      <Dialog.DialogContent className="max-w-4xl">
        <Dialog.DialogHeader>
          <Dialog.DialogTitle className="mx-auto text-xl font-medium tracking-tight">
            Select plan that fits your needs
          </Dialog.DialogTitle>
        </Dialog.DialogHeader>

        <CreateSubscription />
      </Dialog.DialogContent>
    </Dialog.Dialog>
  )
}

function CreateSubscription() {
  const checkoutQuery = useFetchCheckoutUrl()
  const [selectedCadence, setSelectedCadence] = useState<'month' | 'year'>('year')

  const highlightedPlan = 'Growth'
  if (!checkoutQuery.every(query => query.isSuccess)) {
    return <div>Loading...</div>
  }
  return (
    <div>
      <Tabs.Tabs
        value={selectedCadence}
        onValueChange={value => setSelectedCadence(value as 'month' | 'year')}
      >
        <div className="mb-6 text-center">
          <Tabs.TabsList>
            <Tabs.TabsTrigger value="month" className="active:bg-white">
              Monthly
            </Tabs.TabsTrigger>
            <Tabs.TabsTrigger value="year">Yearly</Tabs.TabsTrigger>
          </Tabs.TabsList>
        </div>
        {!checkoutQuery.every(query => query.isSuccess) ? (
          <div>Loading...</div>
        ) : (
          <>
            <Tabs.TabsContent value="month">
              <div className="grid grid-cols-3 gap-x-8">
                {checkoutQuery.map(({ data }) => (
                  <PricingColumn
                    key={data.id}
                    name={data.name}
                    description={data.description}
                    price={data[selectedCadence].price}
                    highlighted={data.name === highlightedPlan}
                    cta={
                      <Button
                        className="w-full"
                        asChild
                        variant={data.name === highlightedPlan ? 'default' : 'outline'}
                      >
                        <a href={data[selectedCadence].url}>
                          {data.name === highlightedPlan
                            ? `Start with ${data.name}`
                            : 'Select plan'}
                        </a>
                      </Button>
                    }
                  ></PricingColumn>
                ))}
              </div>
            </Tabs.TabsContent>
            <Tabs.TabsContent value="year">
              <div className="grid grid-cols-3 gap-x-8">
                {checkoutQuery.map(({ data }) => (
                  <PricingColumn
                    key={data.id}
                    name={data.name}
                    description={data.description}
                    price={data[selectedCadence].price / 12}
                    highlighted={data.name === highlightedPlan}
                    cta={
                      <Button
                        className="w-full"
                        asChild
                        variant={data.name === highlightedPlan ? 'default' : 'outline'}
                      >
                        <a href={data[selectedCadence].url}>
                          {data.name === highlightedPlan
                            ? `Start with ${data.name}`
                            : 'Select plan'}
                        </a>
                      </Button>
                    }
                  ></PricingColumn>
                ))}
              </div>
            </Tabs.TabsContent>
          </>
        )}
      </Tabs.Tabs>
    </div>
  )
}

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'usd',
  compactDisplay: 'short',
  maximumFractionDigits: 0,
})

function PricingColumn(props: PricingColumnProps) {
  const colors = PRODUCT_COLORS[props.name]

  return (
    <div className="row-start-1 row-end-5 grid grid-rows-subgrid rounded-lg border border-border">
      <div className={cn(colors.bg, 'flex flex-col bg-gradient-to-b to-white p-5')}>
        <h3
          className={cn(
            'flex items-center font-pp-supply-sans text-2xl tracking-tight',
            colors.text
          )}
        >
          {props.name}
          {props.highlighted && (
            <div
              className={cn(
                'ml-auto rounded-full px-2 py-0.5 text-right font-sans text-sm text-primary-foreground',
                colors.badgeBg
              )}
            >
              Popular
            </div>
          )}
        </h3>
        <p className="text-sm tracking-tight text-muted-foreground">{props.description}</p>
        <div className="mt-5 text-2xl font-medium tracking-tighter text-base-foreground">
          {/* {fmt.format(
        (selectedCadence === 'year' ? data.year.price / 12 : data.month.price) / 100
      )} */}
          {fmt.format(props.price / 100)}
          <span className="align-middle text-sm tracking-tight text-muted-foreground"> /mo</span>
        </div>
      </div>

      <hr className="mx-5 text-muted-foreground" />
      <div className="flex flex-col justify-stretch space-y-4 px-5 py-6">
        {PLAN_FEATURES[props.name].map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <feature.icon className="h-4 w-4" />
            {feature.label}
          </div>
        ))}
      </div>
      <div className="mt-auto p-5 pt-0">{props.cta}</div>
    </div>
  )
}

type PricingColumnProps = {
  name: string
  description: string
  price: number
  cta: React.ReactNode
  highlighted: boolean
}

const PRODUCT_COLORS: Record<string, { bg: string; text: string; badgeBg: string }> = {
  Starter: {
    bg: 'from-violet-100',
    text: 'text-violet-500',
    badgeBg: 'bg-violet-500',
  },
  Growth: {
    bg: 'from-plum-100',
    text: 'text-plum-500',
    badgeBg: 'bg-plum-500',
  },
  Scale: {
    bg: 'from-orange-100',
    text: 'text-orange-500',
    badgeBg: 'bg-orange-500',
  },
}

const PLAN_FEATURES: Record<string, { icon: LucideIcon; label: React.ReactNode }[]> = {
  Starter: [
    { icon: FolderOpen, label: '7 PoC/month' },
    { icon: CircleUser, label: 'Up to 3 users' },
    { icon: Unplug, label: 'Standard integrations' },
    {
      icon: LayoutDashboard,
      label: <span className="text-base-muted-foreground">Full platform access</span>,
    },
    {
      icon: RefreshCw,
      label: <span className="text-base-muted-foreground">Regular platform updates</span>,
    },
  ],
  Growth: [
    { icon: FolderOpen, label: '25 PoC/month' },
    { icon: CircleUser, label: 'Up to 10 users' },
    { icon: Unplug, label: 'Full platform access' },
    { icon: RefreshCw, label: 'Regular platform updates' },
    {
      icon: LayoutDashboard,
      label: <span className="text-base-muted-foreground">Full platform access</span>,
    },
    {
      icon: RefreshCw,
      label: <span className="text-base-muted-foreground">Regular platform updates</span>,
    },
  ],
  Scale: [
    { icon: FolderOpen, label: '75 PoC/month' },
    { icon: CircleUser, label: 'Unlimited users' },
    { icon: Unplug, label: 'Advanced integrations' },
    { icon: Grid2x2Check, label: 'Custom templates' },
    {
      icon: LayoutDashboard,
      label: <span className="text-base-muted-foreground">Full platform access</span>,
    },
    {
      icon: RefreshCw,
      label: <span className="text-base-muted-foreground">Regular platform updates</span>,
    },
  ],
}
